import { FC } from 'react';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { SopMes } from '@/types/dashboards';
import type { PontoDiario } from '@/utils/equiplexSop';
import { fmtCompacto, fmtData, fmtDiaMes, fmtUn, mesCurto } from '@/utils/painelFormat';
import { useMostrarValores } from '@/hooks/useMostrarValores';
import { mostrarIndices, pontosEmDestaque, valoresDe } from '@/utils/rotulosGrafico';
import BotaoValores from '../BotaoValores';
import { ALTURA_GRAFICO, FONTE_EIXO, corCategoria, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import { rotuloDoPonto } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Props {
  serie: PontoDiario[];
  categorias: string[];
  selecionadas: string[];
  onAlternar: (categoria: string) => void;
  meses: SopMes[];
  corte: string;
  cores: CoresPainel;
}

/**
 * Produzido (reportado nas OPs) por dia de início e categoria de volume. Os chips em cima são a legenda e o
 * filtro ao mesmo tempo; a cor de cada categoria é fixa (não muda com a seleção).
 * Linhas verticais tracejadas marcam a virada do mês de produção (dia 16). Cada linha mostra o
 * número do último dia e dos extremos; "Mostrar valores" liga o de todos os dias.
 */
const ProducaoDiariaChart: FC<Props> = ({ serie, categorias, selecionadas, onAlternar, meses, corte, cores }) => {
  const viradas = meses.slice(1).filter((m) => serie.some((p) => p.data === m.start));
  const [todos, alternarTodos] = useMostrarValores();

  const conteudoTooltip = ({ active, payload, label }: TooltipContentProps) => {
    if (!active || !payload?.length) return null;
    const linhas = selecionadas.map((c) => {
      const entrada = payload.find((e) => e.dataKey === c);
      return {
        rotulo: c,
        valor: fmtUn(typeof entrada?.value === 'number' ? entrada.value : 0),
        cor: corCategoria(c, categorias, cores.modo),
      };
    });
    return <TooltipBox titulo={fmtData(String(label))} linhas={linhas} />;
  };

  return (
    <PainelCard
      testId="sop-chart-diario"
      titulo="Produzido por dia, por volume"
      subtitulo={`Reportado nas OPs, pela data de início, até ${fmtData(corte)}: a régua de todas as abas. Os últimos dias crescem até as OPs serem reportadas.`}
      acoes={<BotaoValores ativo={todos} onAlternar={alternarTodos} testId="sop-chart-diario-valores" />}
    >
      <div role="group" aria-label="Categorias de volume" className="flex flex-wrap gap-1.5">
        {categorias.map((c) => {
          const ativa = selecionadas.includes(c);
          const cor = corCategoria(c, categorias, cores.modo);
          return (
            <button
              key={c}
              type="button"
              aria-pressed={ativa}
              onClick={() => onAlternar(c)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                ativa
                  ? 'border-theme-border bg-surface-hover text-content-primary'
                  : 'border-transparent text-content-muted hover:text-content-primary'
              }`}
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={ativa ? { backgroundColor: cor } : { border: `1.5px solid ${cor}` }}
              />
              {c}
            </button>
          );
        })}
      </div>
      <div style={{ width: '100%', height: ALTURA_GRAFICO }}>
        {serie.length === 0 || selecionadas.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-content-muted">
            {selecionadas.length === 0 ? 'Escolha ao menos uma categoria.' : 'Sem produção no período.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={serie} margin={{ top: 24, right: 40, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis
                dataKey="data"
                tickFormatter={fmtDiaMes}
                tick={tickEixo(cores)}
                axisLine={{ stroke: cores.eixo }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} width={64} />
              {viradas.map((m) => (
                <ReferenceLine
                  key={m.key}
                  x={m.start}
                  stroke={cores.eixo}
                  strokeDasharray="4 4"
                  label={{ value: mesCurto(m.label), position: 'insideTopLeft', fill: cores.textoMuted, fontSize: FONTE_EIXO }}
                />
              ))}
              <Tooltip content={conteudoTooltip} cursor={{ stroke: cores.textoMuted, strokeWidth: 1 }} />
              {selecionadas.map((c) => (
                <Line
                  key={c}
                  type="linear"
                  dataKey={c}
                  name={c}
                  stroke={corCategoria(c, categorias, cores.modo)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--bg-card)' }}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={c}
                    content={rotuloDoPonto(fmtCompacto, cores.texto, mostrarIndices(pontosEmDestaque(valoresDe(serie, c)), todos))}
                  />
                </Line>
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </PainelCard>
  );
};

export default ProducaoDiariaChart;
